import {
	Component,
	EventEmitter,
	Input,
	Output
}                                from '@angular/core';
import { ColorConverterService } from '@shared/components/color-picker/color-converter.service';
import {
	ColorConfigType,
	ColorPickerEvent,
	ColorPickerTypeEnum,
	IColorHueConfig,
	IColorObject,
	IColorPreviewConfig,
	IColorSliderConfig,
	IColorSwatchesConfig,
	ISwatchItem
}                                from '@shared/components/color-picker/color-picker.model';
import { LoggerService }         from '@shared/services/logger.service';
import {
	Color,
	ColorEvent,
	ColorMode,
	RGBA
}                                from 'ngx-color';
import { HSLA }                  from 'ngx-color/helpers/color.interfaces';

@Component( {
	selector: 'app-color-picker',
	templateUrl: './color-picker.component.html',
	styleUrls: [ './color-picker.component.scss' ]
} )
export class ColorPickerComponent {

	private readonly _resetColor: Color = {
		hex: undefined,
		hsl: { h: 180, s: .5, l: .5, a: 1 },
		hsv: { h: undefined, s: undefined, v: undefined, a: undefined },
		oldHue: undefined,
		rgb: { r: undefined, g: undefined, b: undefined, a: undefined },
		source: undefined
	};

	private _selectedColor: Color = this._resetColor;
	private _colorIsReset = false;
	private _colorConfigType: ColorConfigType;

	colorIsSet = false;
	hoverColor: Color;
	colorType: ColorPickerTypeEnum;
	colorTypEnum = ColorPickerTypeEnum;

	// ColorPickerConfigs
	colorHueConfig: IColorHueConfig;
	colorPreviewConfig: IColorPreviewConfig;
	colorSwatchesConfig: IColorSwatchesConfig;
	colorSliderConfig: IColorSliderConfig;

	// ColorPickerSlider
	private _colorSliderSwatchesColorRange: number;
	private _colorSliderSwatchOffset: number;
	private _colorSliderSwatchCount: number;
	private _colorSliderSwatchEndLightness: number;
	private _colorSliderSwatchStartLightness: number;

	@Input() set colorConfigType( colorConfigType: ColorConfigType ) {
		this._colorConfigType = colorConfigType;

		this.colorType = colorConfigType?.type;
		switch ( this.colorType ) {
			case ColorPickerTypeEnum.HUE:
				this.colorHueConfig = {
					...colorConfigType
				} as IColorHueConfig;
				break;

			case ColorPickerTypeEnum.PREVIEW:
				this.colorPreviewConfig = {
					...colorConfigType
				} as IColorPreviewConfig;
				break;

			case ColorPickerTypeEnum.SLIDER:
				this.colorSliderConfig = {
					...colorConfigType
				} as IColorSliderConfig;

				this._colorSliderSwatchCount = this.colorSliderConfig?.swatchCount ?? 5;

				this._colorSliderSwatchEndLightness =
					this.colorSliderConfig?.swatchEndLightness !== undefined && this.colorSliderConfig?.swatchEndLightness !== null
					? this.colorSliderConfig.swatchEndLightness / 100
					: 0;

				this._colorSliderSwatchStartLightness =
					this.colorSliderConfig?.swatchStartLightness !== undefined && this.colorSliderConfig?.swatchStartLightness !== null
					? this.colorSliderConfig.swatchStartLightness / 100
					: 1;

				this._colorSliderSwatchesColorRange = this._colorSliderSwatchEndLightness - this._colorSliderSwatchStartLightness;
				this._colorSliderSwatchOffset = Number.parseFloat(
					(
						this._colorSliderSwatchesColorRange /
						(
							this._colorSliderSwatchCount - 1
						)
					).toFixed( 2 )
				);

				const selectedColor: Color = this.convertColorObjectToColor(
					this.createColorDataFromHSL( 180, .5, .5 )
				);

				this.generateColorSliderSwatches( selectedColor?.hsl );
				break;

			case ColorPickerTypeEnum.SWATCHES:
				this.colorSwatchesConfig = colorConfigType as IColorSwatchesConfig;
				break;
		}

		this._selectedColor = this._selectedColor ?? this._resetColor;
	}

	@Output() selectedColorEmitter: EventEmitter<ColorPickerEvent> = new EventEmitter<ColorPickerEvent>();

	get selectedColor(): Color {
		return this._selectedColor;
	}

	@Input() set selectedColor( selectedColor: Color ) {
		if ( !selectedColor ) {
			this.resetToDefault();
			return;
		}

		this._colorIsReset = false;
		this.init( selectedColor );
	}

	colorModeEnum = ColorMode;

	swatchList: ISwatchItem[];

	activeSwatchItem: ISwatchItem;

	constructor(
		private readonly colorConverterService: ColorConverterService,
		private readonly logger: LoggerService
	) {}


	private init( selectedColor: Color ): void {
		// selectedColor is complete filled
		if (
			selectedColor?.hex &&
			selectedColor?.hsl &&
			selectedColor?.rgb
		) {
			this._selectedColor = selectedColor;
		} else { // selectedColor is not complete filled

			const hslString = selectedColor?.hsl?.h && selectedColor?.hsl?.s && selectedColor?.hsl?.l
												? this.colorConverterService.toHslString( selectedColor?.hsl.h, selectedColor?.hsl.s, selectedColor?.hsl.l )
												: null;

			const rgbString = selectedColor?.rgb?.r && selectedColor?.rgb?.g && selectedColor?.rgb?.b
												? this.colorConverterService.toRgbString( selectedColor?.rgb.r, selectedColor?.rgb.g, selectedColor?.rgb.b )
												: null;

			const color: Color = this.convertColorObjectToColor(
				this.colorConverterService.toColorObject(
					selectedColor?.hex ??
					hslString ??
					rgbString
				) );

			this._selectedColor = {
				...color,
				hex: color?.hex ?? this.createColorDataFromHSL( color.hsl.h, color.hsl.s, color.hsl.l )?.hex
			};
		}

		this.colorIsSet = !this._colorIsReset;

		switch ( this.colorType ) {
			case ColorPickerTypeEnum.SLIDER:
				this.generateColorSliderSwatches( this.selectedColor?.hsl );
				break;
		}
	}

	private resetToDefault() {
		this._colorIsReset = true;
		this.init( this._resetColor );
	}

	private convertColorObjectToColor( colorObject: IColorObject ): Color {
		if ( !colorObject ) { return; }

		return {
			hsl: {
				h: colorObject.hue,
				s: colorObject.sat,
				l: colorObject.lightness,
				a: colorObject.opacity
			},
			hex: colorObject.hex,
			hsv: null,
			rgb: {
				r: colorObject.red,
				g: colorObject.green,
				b: colorObject.blue,
				a: colorObject.opacity
			},
			source: null,
			oldHue: null
		};
	}

	private createColorDataFromHSL( hue: number, saturation: number, lightness: number ): IColorObject {
		const colorObject: IColorObject = this.colorConverterService.roundDecimals(
			this.colorConverterService.toColorObject(
				`hsl(${ hue },${ saturation },${ lightness })`
			)
		);

		const rgba: RGBA = {
			r: colorObject.red,
			g: colorObject.green,
			b: colorObject.blue,
			a: 1
		};

		return {
			...colorObject,
			hex: this.colorConverterService.toHexString( rgba.r, rgba.g, rgba.b )
		};
	}

	private generateColorSliderSwatches( hsla: HSLA ): void {
		let lValue: number = this._colorSliderSwatchEndLightness;
		let activeSwatchItem: ISwatchItem;

		const swatchList: ISwatchItem[] = [];
		for ( let step = 0; step < this._colorSliderSwatchCount; step++ ) {
			const colorObject: IColorObject = this.createColorDataFromHSL( hsla.h, .5, lValue );
			const swatchItem: ISwatchItem = {
				id: step,
				color: this.convertColorObjectToColor( colorObject ),
				isActive: colorObject?.hex === this.selectedColor?.hex,
				hsla,
				offset: Number.parseFloat( lValue.toFixed( 2 ) )
			};

			if ( !activeSwatchItem && swatchItem.isActive ) {
				activeSwatchItem = swatchItem;
			}

			swatchList.push( swatchItem );
			lValue -= this._colorSliderSwatchOffset;
		}

		this.swatchList = swatchList;
		this.activeSwatchItem = activeSwatchItem;
	}

	private emitColorChange(): void {
		this.colorIsSet = true;
		this.selectedColorEmitter.emit(
			{
				type: this._colorConfigType?.type,
				selectedColor: this.selectedColor
			}
		);
	}

	// HUE
	onHueColorChange( colorEvent: ColorEvent ) {
		switch ( this.colorType ) {
			case ColorPickerTypeEnum.SLIDER:
				this.generateColorSliderSwatches( colorEvent?.color?.hsl );
				break;
		}
	}

	// PREVIEW
	getColorStrings( color: Color ): { hsl: string, rgb: string, hex: string } {
		const spareString = '-';

		// HSL
		const h = color?.hsl?.h;
		const s = color?.hsl?.s * 100;
		const l = color?.hsl?.l * 100;
		const hslaString: string = this.colorIsSet &&
															 !Number.isNaN( h ) &&
															 !Number.isNaN( s ) &&
															 !Number.isNaN( l )
															 ? `${ h.toFixed( 0 ) }° ${ s.toFixed( 0 ) }% ${ l.toFixed( 0 ) }%`
															 : spareString;

		// RGB
		const r = color?.rgb?.r ? `${ color?.rgb?.r }` : null;
		const g = color?.rgb?.g ? `${ color?.rgb?.g }` : null;
		const b = color?.rgb?.b ? `${ color?.rgb?.b }` : null;
		const rgbaString: string = this.colorIsSet &&
															 !Number.isNaN( r ) &&
															 !Number.isNaN( g ) &&
															 !Number.isNaN( b )
															 ? `${ r } ${ g } ${ b }`
															 : spareString;

		// HEX
		const hexString: string = this.colorIsSet
															? color?.hex
															: spareString;

		return { hsl: hslaString, rgb: rgbaString, hex: hexString };
	}

	// SLIDER
	onSliderSwatchColorSelect( swatchItemIndex: number ) {
		this.activeSwatchItem = this.swatchList[ swatchItemIndex ];
		this.swatchList = this.swatchList.map( ( swatchItem: ISwatchItem ) => {
			return {
				...swatchItem,
				isActive: swatchItem.color?.hex === this.activeSwatchItem?.color?.hex
			};
		} );

		this.selectedColor = this.activeSwatchItem?.color;
		this.emitColorChange();
	}

	setSliderSwatchHoverColor( swatchItemIndex?: number ) {
		let hoverColor: Color;
		if ( Number.isInteger( swatchItemIndex ) ) {
			this.activeSwatchItem = this.swatchList[ swatchItemIndex ];
			hoverColor = this.activeSwatchItem.color;
		}
		this.hoverColor = hoverColor;
	}

	// SWATCHES
	onSwatchesItemColorSelect( colorEvent: ColorEvent ): void {
		this.selectedColor = colorEvent.color;
		this.generateColorSliderSwatches( colorEvent?.color?.hsl );
		this.logger.log( colorEvent );
		this.emitColorChange();
	}
}
